import React from 'react';
import { Button } from '@/components/ui/button';
import { Lock } from 'phosphor-react';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import {
  HISTORY_CURRENCIES,
  HISTORY_RANGES,
  HISTORY_SORT_MODES,
  HISTORY_STATUSES,
  HISTORY_UNDERLYINGS,
  HISTORY_VIEWS,
  formatHistorySortLabel,
  formatHistoryStatus,
  formatStrategyLabel,
  getEffectiveUnderlyingFilter,
} from './optionsHistoryUtils';
import { getRangeLabel } from './optionsDashboardUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsCurrencyDisplay } from './OptionsCurrencyDisplay';
import { OptionsSyncButton } from './OptionsSyncButton';
import { isOptionsCustomPeriodRestricted, isOptionsHistoryViewRestricted } from './optionsFreemiumUtils';

const renderCurrencyPill = (item) => <OptionsCurrencyDisplay currencyKey={item} compact iconSize={15} />;

export const OptionsHistoryControls = ({
  currentView,
  onViewChange,
  selectedCurrency,
  onCurrencyChange,
  selectedRange,
  onRangeChange,
  customRange,
  onCustomRangeChange,
  onApplyCustomRange,
  underlyingFilter,
  onUnderlyingChange,
  strategyFilter,
  onStrategyChange,
  statusFilter,
  onStatusChange,
  sortMode,
  onSortChange,
  syncing,
  onSync,
  strategyOptions = ['all'],
  showSyncButton = true,
}) => {
  const { t } = useOptionsLocale();
  const effectiveUnderlying = getEffectiveUnderlyingFilter(selectedCurrency, underlyingFilter);
  const hideStatus = currentView === 'rolls';
  const showSort = currentView === 'operations' && sortMode && onSortChange;

  return (
    <div className="space-y-3" data-testid="options-history-controls">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <FilterPills
          label={null}
          items={HISTORY_VIEWS}
          value={currentView}
          onChange={onViewChange}
          formatter={(item) => ({ operations: t('history.views.operations'), campaigns: t('history.views.campaigns'), rolls: t('history.views.rolls') }[item])}
          testIdPrefix="options-history-view"
        />
        {showSyncButton ? (
          <OptionsSyncButton onSync={onSync} syncing={syncing} testId="options-history-sync-button" />
        ) : null}
      </div>

      <div className="grid gap-3 xl:grid-cols-[1fr,1fr,1.1fr]" data-testid="options-history-top-filters">
        <FilterPills
          label={t('history.displayLabel')}
          items={HISTORY_CURRENCIES}
          value={selectedCurrency}
          onChange={onCurrencyChange}
          formatter={renderCurrencyPill}
          testIdPrefix="options-history-currency"
        />
        <FilterPills
          label={t('common.period')}
          items={HISTORY_RANGES}
          value={selectedRange}
          onChange={onRangeChange}
          formatter={(item) => (item === 'all' ? t('history.statuses.all') : getRangeLabel(item, t))}
          testIdPrefix="options-history-range"
        />
        {selectedRange === 'custom' ? (
          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="options-history-custom-range-block">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40" data-typography="label">{t('common.start')}</p>
              <input
                type="date"
                value={customRange.startDate}
                onChange={(event) => onCustomRangeChange((current) => ({ ...current, startDate: event.target.value }))}
                className="h-10 rounded-xl border border-white/10 bg-black/20 px-3 text-sm text-white outline-none"
                data-testid="options-history-custom-start-input"
              />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40" data-typography="label">{t('common.end')}</p>
              <input
                type="date"
                value={customRange.endDate}
                onChange={(event) => onCustomRangeChange((current) => ({ ...current, endDate: event.target.value }))}
                className="h-10 rounded-xl border border-white/10 bg-black/20 px-3 text-sm text-white outline-none"
                data-testid="options-history-custom-end-input"
              />
            </div>
            <Button variant="outline" onClick={onApplyCustomRange} data-testid="options-history-custom-apply-button">
              {t('common.apply')}
            </Button>
          </div>
        ) : null}
      </div>

      <div className={`grid gap-3 ${hideStatus ? 'xl:grid-cols-[1fr,1.3fr]' : 'xl:grid-cols-[1fr,1.3fr,1fr]'}`} data-testid="options-history-bottom-filters">
        <FilterPills
          label={t('common.underlying')}
          items={HISTORY_UNDERLYINGS}
          value={effectiveUnderlying}
          onChange={onUnderlyingChange}
          formatter={(item) => (item === 'all' ? t('history.statuses.all') : renderCurrencyPill(item))}
          testIdPrefix="options-history-underlying"
        />
        <FilterPills
          label={t('common.strategy')}
          items={strategyOptions}
          value={strategyFilter}
          onChange={onStrategyChange}
          formatter={(item) => (item === 'all' ? t('history.statuses.all') : formatStrategyLabel(item))}
          compact
          testIdPrefix="options-history-strategy"
        />
        {!hideStatus ? (
          <FilterPills
            label={t('common.status')}
            items={HISTORY_STATUSES}
            value={statusFilter}
            onChange={onStatusChange}
            formatter={(item) => formatHistoryStatus(item, t)}
            testIdPrefix="options-history-status"
          />
        ) : null}
      </div>

      {showSort ? (
        <FilterPills
          label={t('history.sortLabel')}
          items={HISTORY_SORT_MODES}
          value={sortMode}
          onChange={onSortChange}
          formatter={(item) => formatHistorySortLabel(item, t)}
          compact
          testIdPrefix="options-history-sort"
        />
      ) : null}
    </div>
  );
};

const FilterPills = ({ label, items, value, onChange, formatter, disabled = false, compact = false, testIdPrefix }) => {
  const { isPro, openUpgradeModal } = useOptionsEntitlements();
  
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-block`}>
      {label ? <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40" data-typography="label" data-testid={`${testIdPrefix}-label`}>{label}</p> : null}
      <div className={`inline-flex w-full flex-wrap items-center gap-1.5 rounded-[18px] border border-white/10 bg-[#11161E] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] ${disabled ? 'opacity-60' : ''}`} data-testid={`${testIdPrefix}-group`}>
        {items.map((item) => {
          const active = value === item;
          
          // Verificar restricao para view do Historico
          const isViewRestricted = testIdPrefix === "options-history-view" && isOptionsHistoryViewRestricted({ isPro, view: item });
          
          // Verificar restricao para range do Historico
          const isRangeRestricted = testIdPrefix === "options-history-range" && isOptionsCustomPeriodRestricted({ isPro, range: item });
          
          const isRestricted = isViewRestricted || isRangeRestricted;
          
          const handleClick = () => {
            if (isRestricted) {
              openUpgradeModal(isViewRestricted ? "HISTORY_CYCLES" : "HISTORY");
              return;
            }
            onChange(item);
          };

          return (
            <button
              key={item}
              type="button"
              onClick={handleClick}
              disabled={disabled}
              data-testid={`${testIdPrefix}-${item.toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 rounded-[14px] px-3 py-2 text-sm font-semibold transition-all duration-200 font-body ${compact ? 'text-xs sm:text-sm' : ''} ${active ? 'bg-[#202A38] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : 'text-white/62 hover:bg-white/[0.04] hover:text-white'} disabled:cursor-not-allowed`}
              data-typography="nav"
            >
              {isRestricted && <Lock size={12} className="text-[#A855F7]" weight="fill" />}
              {formatter(item)}
              {isRestricted && <span className="text-[9px] bg-[#A855F7]/10 text-[#A855F7] px-1.5 py-0.5 rounded-sm font-bold">PRO</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OptionsHistoryControls;